import { CATEGORY_SCORING_CONFIG } from "../config/scoring-weights.ts";
import { FACTOR_DIRECTIONS } from "../normalization/config.ts";
import type { FactorName } from "../types/factor.ts";

const FACTOR_LABELS = Object.fromEntries(
  Object.values(CATEGORY_SCORING_CONFIG).flatMap((category) => (
    category.factors.map((factor) => [factor.factor, factor.label])
  )),
) as Record<FactorName, string>;

export type FactorExplanationInput = {
  factorName: FactorName;
  normalizedScore: number | null;
  industryPercentile: number | null;
};

const bounded = (value: number) => Math.min(100, Math.max(0, value));

export function explainFactor(input: FactorExplanationInput): string {
  const { factorName, normalizedScore, industryPercentile } = input;
  const label = FACTOR_LABELS[factorName];
  if (normalizedScore === null || !Number.isFinite(normalizedScore)
    || industryPercentile === null || !Number.isFinite(industryPercentile)) {
    return `${label}数据不足，暂不参与该项解释。`;
  }
  const score = bounded(normalizedScore);
  const percentile = bounded(industryPercentile);
  const direction = FACTOR_DIRECTIONS[factorName];
  const favorableShare = direction === "higher_is_better" ? percentile : 100 - percentile;
  const topShare = Math.max(1, Math.round(100 - favorableShare));
  const bottomShare = Math.max(1, Math.round(favorableShare));
  if (score >= 80) return direction === "higher_is_better"
    ? `${label}位于同行业前${topShare}%，相对表现较强。`
    : `${label}低于大多数同行公司，位于相对有利的前${topShare}%。`;
  if (score >= 60) return `${label}优于约${Math.round(favorableShare)}%的同行公司，相对表现偏强。`;
  if (score >= 40) return `${label}处于同行业中游，暂未形成明显优势或短板。`;
  if (score >= 20) return `${label}弱于约${Math.round(100 - favorableShare)}%的同行公司，需要持续观察。`;
  return direction === "higher_is_better"
    ? `${label}位于同行业后${bottomShare}%，相对表现较弱。`
    : `${label}高于大多数同行公司，位于相对不利的后${bottomShare}%。`;
}
